"use client";

import { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "react-toastify";
import { useLanguage } from "@/utils/LanguageContext";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

interface FormData {
  user_name: string;
  user_email: string;
  user_phone: string;
  user_company: string;
  message: string;
}

const schema = yup
  .object({
    user_name: yup
      .string()
      .trim()
      .max(80, "Name is too long")
      .required("Name is required"),
    user_email: yup
      .string()
      .trim()
      .max(160, "Email is too long")
      .email("Enter a valid email")
      .required("Email is required"),
    user_phone: yup
      .string()
      .trim()
      .max(40, "Phone number is too long")
      .matches(/^$|^[0-9+().\-\s]{7,40}$/, "Enter a valid phone number")
      .defined(),
    user_company: yup.string().trim().max(120, "Company name is too long").defined(),
    message: yup
      .string()
      .trim()
      .max(2000, "Message is too long")
      .required("Message is required"),
  })
  .required();

type SubmitState = "idle" | "success" | "error";

const ContactForm = () => {
  const { t } = useLanguage();
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const formRef = useRef<HTMLFormElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      user_name: "",
      user_email: "",
      user_phone: "",
      user_company: "",
      message: "",
    },
  });

  const getErrorId = (fieldName: keyof FormData) => `${fieldName}-error`;

  const sendEmail = async () => {
    if (!formRef.current) {
      setSubmitState("error");
      toast.error(t("Unexpected error occurred"));
      return;
    }

    const serviceId = process.env.NEXT_PUBLIC_EMAILJS_SERVICE_ID as string | undefined;
    const templateId = process.env.NEXT_PUBLIC_EMAILJS_TEMPLATE_ID as string | undefined;
    const publicKey = process.env.NEXT_PUBLIC_EMAILJS_PUBLIC_KEY as string | undefined;

    if (!serviceId || !templateId || !publicKey) {
      console.error("EmailJS environment variables are missing.");
      setSubmitState("error");
      toast.error(t("Unexpected error occurred"));
      return;
    }

    setSubmitState("idle");

    try {
      await emailjs.sendForm(serviceId, templateId, formRef.current, publicKey);
      setSubmitState("success");
      toast.success(t("Comment sent successfully"), { position: "top-center" });
      reset();
    } catch (error) {
      console.error("Failed to submit contact form.", error);
      setSubmitState("error");
      toast.error(t("Unexpected error occurred"));
    }
  };

  return (
    <form
      ref={formRef}
      className="contact-form-fields"
      onSubmit={handleSubmit(sendEmail)}
      noValidate
      aria-describedby="contact-form-status"
      aria-busy={isSubmitting}
    >
      <div className="contact-form-grid">
        <div className="contact-field">
          <label htmlFor="user_name">{t("Name")}</label>
          <input
            id="user_name"
            type="text"
            autoComplete="name"
            maxLength={80}
            placeholder={t("Enter you name")}
            aria-invalid={Boolean(errors.user_name)}
            aria-describedby={errors.user_name ? getErrorId("user_name") : undefined}
            {...register("user_name")}
          />
          <p id={getErrorId("user_name")} className="form-error" role="alert">
            {errors.user_name?.message || "\u00a0"}
          </p>
        </div>

        <div className="contact-field">
          <label htmlFor="user_email">{t("Email")}</label>
          <input
            id="user_email"
            type="email"
            autoComplete="email"
            maxLength={160}
            placeholder={t("Enter you email")}
            aria-invalid={Boolean(errors.user_email)}
            aria-describedby={errors.user_email ? getErrorId("user_email") : undefined}
            {...register("user_email")}
          />
          <p id={getErrorId("user_email")} className="form-error" role="alert">
            {errors.user_email?.message || "\u00a0"}
          </p>
        </div>

        <div className="contact-field">
          <label htmlFor="user_company">{t("Company")}</label>
          <input
            id="user_company"
            type="text"
            autoComplete="organization"
            maxLength={120}
            placeholder={t("Company")}
            aria-invalid={Boolean(errors.user_company)}
            aria-describedby={errors.user_company ? getErrorId("user_company") : undefined}
            {...register("user_company")}
          />
          <p id={getErrorId("user_company")} className="form-error" role="alert">
            {errors.user_company?.message || "\u00a0"}
          </p>
        </div>

        <div className="contact-field">
          <label htmlFor="user_phone">{t("Mobile no")}</label>
          <input
            id="user_phone"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            maxLength={40}
            placeholder={t("Mobile no")}
            aria-invalid={Boolean(errors.user_phone)}
            aria-describedby={errors.user_phone ? getErrorId("user_phone") : undefined}
            {...register("user_phone")}
          />
          <p id={getErrorId("user_phone")} className="form-error" role="alert">
            {errors.user_phone?.message || "\u00a0"}
          </p>
        </div>
      </div>

      <div className="contact-field">
        <label htmlFor="message">{t("Message")}</label>
        <textarea
          id="message"
          rows={7}
          maxLength={2000}
          placeholder={t("Enter you message...")}
          aria-invalid={Boolean(errors.message)}
          aria-describedby={errors.message ? getErrorId("message") : undefined}
          {...register("message")}
        ></textarea>
        <p id={getErrorId("message")} className="form-error" role="alert">
          {errors.message?.message || "\u00a0"}
        </p>
      </div>

      <div className="contact-form-actions">
        <button type="submit" className="contact-submit-btn" disabled={isSubmitting}>
          {t("Send Message")}
        </button>
        <p
          id="contact-form-status"
          className={`contact-form-status contact-form-status--${submitState}`}
          role="status"
          aria-live="polite"
        >
          {submitState === "success" && t("Comment sent successfully")}
          {submitState === "error" && t("Unexpected error occurred")}
          {submitState === "idle" && "\u00a0"}
        </p>
      </div>

      <style jsx>{`
        .contact-form-fields {
          position: relative;
          z-index: 1;
        }

        .contact-form-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 0.4rem 1rem;
        }

        .contact-field {
          display: flex;
          flex-direction: column;
        }

        .contact-field label {
          margin-bottom: 0.55rem;
          color: rgba(218, 234, 247, 0.88);
          font-size: 0.92rem;
          font-weight: 600;
        }

        .contact-field input,
        .contact-field textarea {
          width: 100%;
          border: 1px solid rgba(189, 213, 231, 0.16);
          border-radius: 16px;
          background:
            linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.03)),
            rgba(4, 16, 29, 0.52);
          color: #f1f7ff;
          padding: 0.95rem 1rem;
          transition:
            border-color 0.2s ease,
            box-shadow 0.2s ease,
            background 0.2s ease;
        }

        .contact-field input {
          min-height: 54px;
        }

        .contact-field textarea {
          min-height: 180px;
          resize: vertical;
        }

        .contact-field input::placeholder,
        .contact-field textarea::placeholder {
          color: rgba(189, 213, 231, 0.46);
        }

        .contact-field input:focus,
        .contact-field textarea:focus {
          outline: none;
          border-color: rgba(120, 185, 255, 0.42);
          box-shadow: 0 0 0 4px rgba(30, 136, 229, 0.14);
          background:
            linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.04)),
            rgba(4, 16, 29, 0.68);
        }

        .contact-field input[aria-invalid="true"],
        .contact-field textarea[aria-invalid="true"] {
          border-color: rgba(255, 137, 154, 0.48);
          box-shadow: 0 0 0 4px rgba(255, 102, 132, 0.08);
        }

        .form-error {
          min-height: 1.35rem;
          margin: 0.5rem 0 0;
          color: #ff9dad;
          font-size: 0.86rem;
          line-height: 1.4;
        }

        .contact-form-actions {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          margin-top: 0.4rem;
        }

        .contact-submit-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: min(240px, 100%);
          min-height: 54px;
          padding: 0.95rem 1.45rem;
          border: 1px solid rgba(120, 185, 255, 0.32);
          border-radius: 16px;
          background:
            linear-gradient(135deg, rgba(33, 105, 184, 0.96), rgba(12, 58, 108, 0.94)),
            rgba(10, 30, 58, 0.92);
          box-shadow:
            0 16px 34px rgba(4, 14, 32, 0.34),
            inset 0 1px 0 rgba(255, 255, 255, 0.16);
          color: #eef7ff;
          font-weight: 700;
          letter-spacing: 0.01em;
          cursor: pointer;
          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease,
            border-color 0.2s ease,
            background 0.2s ease;
        }

        .contact-submit-btn:hover,
        .contact-submit-btn:focus-visible {
          transform: translateY(-1px);
          border-color: rgba(153, 206, 255, 0.48);
          background:
            linear-gradient(135deg, rgba(41, 121, 213, 0.98), rgba(16, 72, 133, 0.96)),
            rgba(10, 30, 58, 0.96);
          box-shadow:
            0 18px 38px rgba(4, 14, 32, 0.4),
            0 0 0 1px rgba(146, 204, 255, 0.18);
        }

        .contact-submit-btn:focus-visible {
          outline: none;
        }

        .contact-submit-btn:disabled {
          opacity: 0.62;
          cursor: wait;
          transform: none;
          box-shadow: none;
        }

        .contact-form-status {
          min-height: 1.5rem;
          margin: 0.9rem 0 0;
          font-size: 0.92rem;
          line-height: 1.45;
        }

        .contact-form-status--success {
          color: #8ce2a9;
        }

        .contact-form-status--error {
          color: #ff9dad;
        }

        @media (max-width: 767px) {
          .contact-form-grid {
            grid-template-columns: minmax(0, 1fr);
          }

          .contact-submit-btn {
            width: 100%;
          }
        }
      `}</style>
    </form>
  );
};

export default ContactForm;
